import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import Header from "../../../components/Header";
import AdminService from "../../../api/services/adminService";
import { clearAuth, persistAuthForNewTab } from "../../../utils/authStorage";
import { hasPermission, isMasterAdmin, PERMISSION_KEYS as P } from "../../../utils/permissions";
import "./DashboardPage.css";

const DEFAULT_PENDING = {
  deposit: 0,
  withdrawal: 0,
  support: 0,
};

const DashboardPage = () => {
  const [isSidebar, setIsSidebar] = useState(true);
  const [pendingKyc, setPendingKyc] = useState(0);
  const [pendingDeposit, setPendingDeposit] = useState(DEFAULT_PENDING.deposit);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(DEFAULT_PENDING.withdrawal);
  const [pendingSupport] = useState(DEFAULT_PENDING.support);

  const [platformFeatures, setPlatformFeatures] = useState({});

  const fetchPendingDepositCount = useCallback(async () => {
    try {
      const res = await AdminService.getGlobalWalletDeposits({
        status: "pending",
        limit: 1,
        page: 1,
      });
      const total =
        res?.pagination?.total ??
        res?.totalCount ??
        res?.total ??
        (Array.isArray(res?.data) ? res.data.length : 0);
      setPendingDeposit(Number(total) || 0);
    } catch {
      setPendingDeposit(0);
    }
  }, []);

  const fetchPendingWithdrawalCount = useCallback(async () => {
    try {
      const res = await AdminService.getCoboWithdrawalHistory({
        status: "Pending",
        limit: 1,
        page: 1,
      });
      const total =
        res?.pagination?.total ??
        res?.totalCount ??
        res?.total ??
        (Array.isArray(res?.data) ? res.data.length : 0);
      setPendingWithdrawal(Number(total) || 0);
    } catch {
      setPendingWithdrawal(0);
    }
  }, []);

  const fetchDashboardCounts = useCallback(async () => {
    const res = await AdminService.getDashboardCounts();
    if (res?.success && res.data) {
      const kycCount = res.data.pending_kyc;
      setPendingKyc(kycCount != null ? Number(kycCount) : 0);

      const depositCount =
        res.data.pending_deposit ??
        res.data.pending_deposits ??
        res.data.deposit_pending;
      if (depositCount != null && !Number.isNaN(Number(depositCount))) {
        setPendingDeposit(Number(depositCount));
      } else {
        await fetchPendingDepositCount();
      }

      const withdrawalCount =
        res.data.pending_withdrawal ??
        res.data.pending_withdrawals ??
        res.data.withdrawal_pending;
      if (withdrawalCount != null && !Number.isNaN(Number(withdrawalCount))) {
        setPendingWithdrawal(Number(withdrawalCount));
      } else {
        await fetchPendingWithdrawalCount();
      }
    } else {
      await Promise.all([fetchPendingDepositCount(), fetchPendingWithdrawalCount()]);
    }
  }, [fetchPendingDepositCount, fetchPendingWithdrawalCount]);

  const fetchPlatformConfig = useCallback(async () => {
    const res = await AdminService.getPlatformConfig();
    if (res?.success && res.data) {
      const data = res.data;
      const normalized = {};
      ["spot_trading", "futures_trading", "p2p_trading", "staking", "launchpad", "swap", "deposit", "withdrawal", "full_maintenance"].forEach((key) => {
        const v = data[key];
        normalized[key] = v && typeof v === "object" ? !!v.enabled : !!v;
      });
      setPlatformFeatures(normalized);
    }
  }, []);

  useEffect(() => {
    fetchDashboardCounts();
    fetchPlatformConfig();
  }, [fetchDashboardCounts, fetchPlatformConfig]);

  useEffect(() => {
    persistAuthForNewTab();
  }, []);

  useEffect(() => {
    const handler = () => fetchPlatformConfig();
    window.addEventListener("platformConfigUpdated", handler);
    return () => window.removeEventListener("platformConfigUpdated", handler);
  }, [fetchPlatformConfig]);

  useEffect(() => {
    const handler = () => fetchDashboardCounts();
    window.addEventListener("dashboardCountsUpdated", handler);
    return () => window.removeEventListener("dashboardCountsUpdated", handler);
  }, [fetchDashboardCounts]);

  const location = useLocation();
  const isInitialMount = useRef(true);

  // Close sidebar on mobile when route changes (e.g. after clicking a nav link)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const isMobile = window.innerWidth <= 992;
    if (isMobile) {
      setIsSidebar(false);
    }
  }, [location.pathname]);

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const actived = pathSegments[pathSegments.length - 1] || "homepage";

  const toggleSidebar = () => setIsSidebar((prev) => !prev);

  const isActive = (slug) => {
    if (!slug) return false;
    if (slug === "commission-report") {
      return location.pathname.includes("/commission-report");
    }
    if (slug === "downline-view") {
      return location.pathname.includes("/referral/downline-view");
    }
    if (slug === "staking-statement") {
      return location.pathname.includes("/staking/statement");
    }
    if (slug === "round-release-report") {
      return location.pathname.includes("/referral/round-release-report");
    }
    if (slug === "round-releases") {
      return (
        location.pathname.includes("/referral/round-releases") &&
        !location.pathname.includes("round-release-report")
      );
    }
    if (slug === "master-applications") {
      return location.pathname.includes("/copy-trading/master-applications");
    }
    if (slug === "copy-trading") {
      return (
        location.pathname.includes("/dashboard/copy-trading") &&
        !location.pathname.includes("/master-applications")
      );
    }
    return actived === slug;
  };

  const BadgeCount = ({ count }) => {
    if (count == null || count <= 0) return null;
    return (
      <span className="sidebar-pending-badge">
        {count > 99 ? "99+" : count}
      </span>
    );
  };

  const FeatureStatusBadge = ({ featureKey }) => {
    if (!featureKey) return null;
    const enabled = platformFeatures[featureKey];
    if (enabled == null) return null;
    const isMaintenance = featureKey === "full_maintenance";
    const label = isMaintenance ? (enabled ? "Maintenance" : "Active") : (enabled ? "On" : "Off");
    const modifier = isMaintenance ? (enabled ? "maintenance" : "active") : (enabled ? "on" : "off");
    return (
      <span className={`sidebar-feature-badge sidebar-feature-badge--${modifier}`}>
        {label}
      </span>
    );
  };

  const sectionLabel = (text, featureKey = null) => (
    <li key={`section-${text}`} className="sidebar-section-label">
      <span>{text}</span>
      {featureKey && <FeatureStatusBadge featureKey={featureKey} />}
    </li>
  );

  const navLink = (to, slug, label, icon, count = null, featureKey = null) => (
    <li key={slug || to} className={isActive(slug) ? "active" : ""}>
      <Link
        to={to}
        onClick={() => {
          if (window.innerWidth <= 992) setIsSidebar(false);
        }}
      >
        <i className={`fas ${icon}`} />
        <span>{label}</span>
        {count != null && count > 0 && <BadgeCount count={count} />}
        {featureKey && <FeatureStatusBadge featureKey={featureKey} />}
      </Link>
    </li>
  );

  const handleLogout = (e) => {
    e.preventDefault();
    clearAuth();
    sessionStorage.clear();
    window.location.href = "/";
  };

  const scrollContainerRef = useRef(null);
  const savedScrollRef = useRef(0);
  const prevPathnameRef = useRef(location.pathname);

  useLayoutEffect(() => {
    if (location.pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = location.pathname;
      savedScrollRef.current = 0;
      return;
    }
    const el = scrollContainerRef.current;
    const saved = savedScrollRef.current;
    if (el && saved > 0) {
      el.scrollTop = saved;
      const rafId = requestAnimationFrame(() => {
        el.scrollTop = saved;
      });
      return () => cancelAnimationFrame(rafId);
    }
  });

  const handleScrollSave = useCallback((e) => {
    const next = e.target.scrollTop;
    const prev = savedScrollRef.current;
    if (!(prev > 100 && next === 0)) savedScrollRef.current = next;
  }, []);

  return (
    <div className="dashboard-wrapper">
      <Header isSidebar={isSidebar} setIsSidebar={setIsSidebar} onLogout={handleLogout} />
      <div
        id="layoutSidenav"
        className={!isSidebar ? "sidebar-collapsed" : ""}
      >
        <div id="layoutSidenav_nav" className="leftside_menu sidebar-premium">
          <div className="leftside_items">
            {/* <div className="logo sidebar-logo">
              <Link
                to="/dashboard/homepage"
                onClick={() => setIsSidebar(true)}
              >
                <img
                  src="/logo192.png"
                  alt="Logo"
                  loading="lazy"
                />
              </Link>
            </div> */}
            <button
              type="button"
              className="toggle_menu"
              id="toggleBtn"
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
            >
              <i className="fas fa-bars" />
            </button>
            <div className="navi_sidebar" id="content">
              <button
                type="button"
                className="toggle_menu close_btn"
                id="closeBtn"
                onClick={toggleSidebar}
                aria-label="Close sidebar"
              >
                <i className="fas fa-times" />
              </button>
              <div className="responsive_scrool">
                <ul className="list-unstyled ps-0">
                  {sectionLabel("Main")}
                  {hasPermission(P.USERS) &&
                    navLink("/dashboard/homepage", "homepage", "Dashboard", "fa-th")}

                  {isMasterAdmin() && sectionLabel("Admin")}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/listsubadmin",
                      "listsubadmin",
                      "Sub Admin",
                      "fa-user-shield"
                    )}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/admin-logs",
                      "admin-logs",
                      "Admin Logs",
                      "fa-clipboard-list"
                    )}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/admin-settings",
                      "admin-settings",
                      "Payout & Withdrawal",
                      "fa-power-off"
                    )}

                  {hasPermission(P.USERS) && sectionLabel("Traders")}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/tradelist",
                      "tradelist",
                      "Traders List",
                      "fa-user-tie"
                    )}
                  {/* {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/unverified-traders",
                      "unverified-traders",
                      "Unverified users",
                      "fa-user-clock"
                    )} */}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/blocked-traders",
                      "blocked-traders",
                      "Blocked trader list",
                      "fa-user-lock"
                    )}

                  {/* {hasPermission(P.COPY_TRADING) && sectionLabel("Copy Trading")}
                  {hasPermission(P.COPY_TRADING) &&
                    navLink(
                      "/dashboard/copy-trading/master-applications",
                      "master-applications",
                      "Master Applications",
                      "fa-inbox"
                    )}
                  {hasPermission(P.COPY_TRADING) &&
                    navLink(
                      "/dashboard/copy-trading",
                      "copy-trading",
                      "Copy Trading Management",
                      "fa-clone"
                    )} */}

                  {hasPermission(P.KYC) && sectionLabel("KYC")}
                  {hasPermission(P.KYC) &&
                    navLink(
                      "/dashboard/pendingkyc",
                      "pendingkyc",
                      "KYC Management",
                      "fa-id-card",
                      pendingKyc
                    )}

                  {/*                   {(permissions.includes(8) || userType === "1") &&
                    sectionLabel("Coin List Requests")}
                  {(permissions.includes(8) || userType === "1") &&
                    navLink(
                      "/dashboard/coinListDetails",
                      "coinListDetails",
                      "Coin Listed Details",
                      "fa-coins"
                    )}*/}
                

                  {hasPermission(P.USERS) && sectionLabel("Spot Trading", "spot_trading")}

                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/addSpotTradingCoin",
                      "addSpotTradingCoin",
                      "Add Spot Trading Coin",
                      "fa-plus-circle"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/currencymanagement",
                      "currencymanagement",
                      "Spot Currency Management",
                      "fa-chart-line"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/addCurrencyPair",
                      "addCurrencyPair",
                      "Add Spot Pair",
                      "fa-plus-circle"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/currencypair",
                      "currencypair",
                      "Spot Pair Management",
                      "fa-chart-line"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/spotMarketTrades",
                      "spotMarketTrades",
                      "Market Trades",
                      "fa-chart-line"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/OrderBook",
                      "OrderBook",
                      "OrderBook",
                      "fa-book"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/allOpenOrders",
                      "allOpenOrders",
                      "Spot Open Orders",
                      "fa-list"
                    )}

                  {hasPermission(P.USERS) && sectionLabel("Futures Trading", "futures_trading")}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/futuresPair",
                      "futuresPair",
                      "Futures Pair Management",
                      "fa-chart-line"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/futuresOpenOrders",
                      "futuresOpenOrders",
                      "Futures Open Orders",
                      "fa-list"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/futuresPositions",
                      "futuresPositions",
                      "Futures Positions",
                      "fa-bullseye"
                    )}
                  {hasPermission(P.USERS) &&
                    navLink(
                      "/dashboard/futuresTradeHistory",
                      "futuresTradeHistory",
                      "Futures Trade History",
                      "fa-history"
                    )}

                  {hasPermission(P.STAKING) && sectionLabel("Staking", "staking")}
                  {hasPermission(P.STAKING) &&
                    navLink(
                      "/dashboard/staking-packages",
                      "staking-packages",
                      "Staking Packages",
                      "fa-coins"
                    )}
                  {hasPermission(P.STAKING) &&
                    navLink(
                      "/dashboard/staking/add-package",
                      "add-package",
                      "Add Staking Package",
                      "fa-plus-circle"
                    )}
                  {hasPermission(P.STAKING) &&
                    navLink(
                      "/dashboard/staking/user-stakes",
                      "user-stakes",
                      "User Stakes",
                      "fa-list"
                    )}
                  {hasPermission(P.STAKING) &&
                    navLink(
                      "/dashboard/staking/statement",
                      "staking-statement",
                      "Staking Statement",
                      "fa-file-invoice"
                    )}
                  {/* {hasPermission(P.STAKING) &&
                    navLink(
                      "/dashboard/staking/cancelled",
                      "cancelled",
                      "Cancelled Staking",
                      "fa-ban"
                    )} */}
                  {hasPermission(P.STAKING) &&
                    navLink(
                      "/dashboard/staking/stake-report",
                      "stake-report",
                      "Stake Report",
                      "fa-file-alt"
                    )}
                  {hasPermission(P.STAKING) &&
                    navLink(
                      "/dashboard/staking/subscribe-by-admin",
                      "subscribe-by-admin",
                      "By admin",
                      "fa-user-shield"
                    )}

                  {(hasPermission(P.DEPOSITS) || hasPermission(P.WITHDRAWALS)) &&
                    sectionLabel("Funds", null)}
                  {hasPermission(P.DEPOSITS) &&
                    navLink(
                      "/dashboard/fundsDManagement",
                      "fundsDManagement",
                      "Deposit",
                      "fa-arrow-down",
                      pendingDeposit,
                      "deposit"
                    )}
                  {hasPermission(P.DEPOSITS) &&
                    navLink(
                      "/dashboard/deposit-summary",
                      "deposit-summary",
                      "Deposit Summary",
                      "fa-chart-pie"
                    )}
                  {hasPermission(P.WITHDRAWALS) &&
                    navLink(
                      "/dashboard/fundsWithdrawalManagement",
                      "fundsWithdrawalManagement",
                      "Funds Withdrawal",
                      "fa-arrow-up",
                      pendingWithdrawal,
                      "withdrawal"
                    )}

                  {isMasterAdmin() && sectionLabel("Exchange")}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/TradingCommision",
                      "TradingCommision",
                      "Trading Commission",
                      "fa-chart-pie"
                    )}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/WithdrawalFees",
                      "WithdrawalFees",
                      "Withdrawal Fees",
                      "fa-percent"
                    )}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/exchangeWalletManagement",
                      "exchangeWalletManagement",
                      "Exchange Wallet",
                      "fa-wallet"
                    )}
                  {hasPermission(P.LAUNCHPAD) && !isMasterAdmin() && sectionLabel("Launchpad", "launchpad")}
                  {hasPermission(P.LAUNCHPAD) &&
                    navLink(
                      "/dashboard/LaunchpadManagement",
                      "LaunchpadManagement",
                      "Launchpad Management",
                      "fa-rocket",
                      null,
                      "launchpad"
                    )}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/AdminDebitCreditTrans",
                      "AdminDebitCreditTrans",
                      "Admin Debit Credit",
                      "fa-exchange-alt"
                    )}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/AnnouncementDetails",
                      "AnnouncementDetails",
                      "Announcement",
                      "fa-bullhorn"
                    )}

                  {/* {userType === "1" && sectionLabel("P2P", "p2p_trading")}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/p2p-orders",
                      "p2p-orders",
                      "P2P Orders",
                      "fa-exchange-alt"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/p2p-ads",
                      "p2p-ads",
                      "P2P Ads",
                      "fa-ad"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/dispute_management",
                      "dispute_management",
                      "Dispute Management",
                      "fa-gavel"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/p2p-fiat-management",
                      "p2p-fiat-management",
                      "Fiat Currency",
                      "fa-money-bill"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/p2p-user-payment-methods",
                      "p2p-user-payment-methods",
                      "User Payment Methods",
                      "fa-credit-card"
                    )} */}

                  {(hasPermission(P.SUPPORT) || hasPermission(P.REFERRAL) || isMasterAdmin()) &&
                    sectionLabel("System")}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/notification",
                      "notification",
                      "Notifications",
                      "fa-bell"
                    )}
                  {isMasterAdmin() &&
                    navLink(
                      "/dashboard/send-email",
                      "send-email",
                      "Send Email",
                      "fa-envelope"
                    )}
                  {hasPermission(P.SUPPORT) &&
                    navLink(
                      "/dashboard/support",
                      "support",
                      "Support",
                      "fa-headset",
                      pendingSupport
                    )}
                  {hasPermission(P.SUPPORT) &&
                    navLink(
                      "/dashboard/UpdateApk",
                      "UpdateApk",
                      "Update Apk",
                      "fa-mobile-alt"
                    )}
                  {hasPermission(P.REFERRAL) && sectionLabel("Referral")}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/bonus-management",
                      "bonus-management",
                      "Bonus Management",
                      "fa-gift"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/referral-management",
                      "referral-management",
                      "Referral Management",
                      "fa-network-wired"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/upline-downline",
                      "upline-downline",
                      "Upline & Downline",
                      "fa-project-diagram"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/commission-report",
                      "commission-report",
                      "Commission Report",
                      "fa-chart-pie"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/referral/downline-view",
                      "downline-view",
                      "Downline View",
                      "fa-layer-group"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/referral-release-plans",
                      "referral-release-plans",
                      "Referral Release Plans",
                      "fa-sitemap"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/referral/round-release-report",
                      "round-release-report",
                      "Round Release Report",
                      "fa-list-alt"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/referral/round-releases",
                      "round-releases",
                      "Round Release Ledger",
                      "fa-book"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/manage-bonus-funds",
                      "manage-bonus-funds",
                      "Manage bonus funds",
                      "fa-hand-holding-usd"
                    )}
                  {hasPermission(P.REFERRAL) &&
                    navLink(
                      "/dashboard/brokerage",
                      "brokerage",
                      "Brokerage",
                      "fa-coins"
                    )}
                </ul>
              </div>
              <div className="logout_btn">
                <a href="#/" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt" />
                  <span>Logout</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div
          ref={scrollContainerRef}
          className="layoutSidenav_content"
          onScroll={handleScrollSave}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
